import { Box, Button } from '@mui/material'
import React, { useCallback, useEffect } from 'react'
import { ExtensionConnectionTypes, ExtensionState } from 'src/Libs/installation-extensions/types'
import { DisconnectButton } from '../../../general'
import useInstallationExtensionData from 'src/Libs/installation-extensions/hooks/extensions/use-installation-extension-data'
import { useTypedSelector } from 'src/State/RootReducer'
import useModals from 'src/Hooks/useModals'
import { OptionalTooltipWrapper } from '@e3dc-react/shell/Elements/Tooltip'
import { makeStyles } from 'tss-react/mui'
import { comparePortalVersions, getWebviewPortalVersion, VersionComparison } from '@e3dc-react/shell/Libs/webview'
import LinkVariant from 'src/Elements/Buttons/LinkVariant'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useExtensionConnection } from 'src/Libs/installation-extensions/hooks'
import { sendMessageToReactNative } from '@e3dc-react/shell/Libs/react-native'
import { SeparatorLineWithText } from 'src/Elements/Separator'
import { useIsWebviewMode } from 'src/Hooks'

const useStyles = makeStyles<{ isDisabled?: boolean }>()((theme, props) => ({
  button: {
    borderRadius: theme.spacing(12),
    textDecoration: 'none',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  disabledButton: {
    backgroundColor: theme.palette.grey.grey06,
    borderColor: theme.palette.grey.grey06,
    pointerEvents: 'none',
    textDecoration: 'none',
  },
  link: {
    padding: theme.spacing(1),
    cursor: 'pointer',
  },
  connectionLink: {
    color: props.isDisabled ? theme.palette.grey.grey03 : 'inherit',
    cursor: 'pointer',
  },
}))

interface Props {
  extensionState?: ExtensionState
  hasActiveTariff?: boolean
}

const ADVERTISEMENT_LINK =
  'https://stromrechner.luox-energy.de?spid=edreidc&utm_source=partner&utm_medium=referral&utm_content=affiliate-link&utm_campaign=edreidc'

export const PORTAL_VERSION_TO_BE_ABLE_TO_CONNECT_CONTRACT = '1.0.9'

/**
 * Footer content of the luox detail view. Allows to access a revenue calculator and displays the contract status.
 */
const FooterContent: React.FC<Props> = ({ extensionState, hasActiveTariff }) => {
  const { t } = useCustomTranslation()
  const installationId = useTypedSelector(state => state.storage.activeInstallation)?.toString() ?? ''
  const { loadInstallationExtensionStates } = useInstallationExtensionData(installationId)
  const { openLuoxDynamicTariffDeleteConnectionModal } = useModals()
  const disabledButtonTooltipKey = `installation-extensions.luox-dynamic-tariff.connection-button.disabled-tooltip`
  const isWebview = useIsWebviewMode()
  const isActive = extensionState === 'active'
  const webViewPortalVersion = getWebviewPortalVersion()
  const disabledConnectDueToAppVersion: boolean =
    isWebview &&
    !!webViewPortalVersion &&
    comparePortalVersions(webViewPortalVersion, PORTAL_VERSION_TO_BE_ABLE_TO_CONNECT_CONTRACT) === VersionComparison.Lesser
  const { createExtensionConnectionUrl: createLuoxConnectionUrl, extensionConnectionUrl: luoxConnectionUrl } = useExtensionConnection(
    ExtensionConnectionTypes.Luox
  )
  const isConnectDisabled = hasActiveTariff || disabledConnectDueToAppVersion

  const { classes, cx } = useStyles({ isDisabled: isConnectDisabled })

  useEffect(() => {
    if (!luoxConnectionUrl) createLuoxConnectionUrl()
  }, [luoxConnectionUrl]) //eslint-disable-line

  const handleOnClick = useCallback((): void => {
    if (luoxConnectionUrl) {
      sendMessageToReactNative({ type: 'luox-url', url: luoxConnectionUrl })
    }
  }, [luoxConnectionUrl])

  const handleDisconnectExtension = (): void => {
    openLuoxDynamicTariffDeleteConnectionModal({ loadInstallationExtensionStates })
  }

  const getTooltipKey = (): string | undefined => {
    if (hasActiveTariff) return disabledButtonTooltipKey
    if (disabledConnectDueToAppVersion) return `installation-extensions.luox-dynamic-tariff.connection-button.disabled-tooltip.version`
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, width: '100%' }}>
      {!isActive && (
        <>
          <OptionalTooltipWrapper hasTooltip={isConnectDisabled} titleKey={getTooltipKey()}>
            <Button
              className={isConnectDisabled ? classes.disabledButton : classes.button}
              onClick={isWebview ? handleOnClick : undefined}
              disabled={isConnectDisabled}
            >
              <LinkVariant
                type="link"
                link={!isWebview && luoxConnectionUrl ? luoxConnectionUrl : undefined}
                hasUnderline={false}
                textClassName={cx(classes.link, classes.connectionLink)}
                label={t('installation-extensions.luox-dynamic-tariff.details.link-text')}
              />
            </Button>
          </OptionalTooltipWrapper>
          <SeparatorLineWithText text={t('installation-extensions.luox-dynamic-tariff.details.separator-text')} />
          <LinkVariant
            type="link"
            hasUnderline={false}
            label={t('installation-extensions.luox-dynamic-tariff.details.button-text')}
            textClassName={cx(classes.link)}
            link={ADVERTISEMENT_LINK}
          />
        </>
      )}
      <DisconnectButton isConnected={isActive} onClickLink={handleDisconnectExtension} />
    </Box>
  )
}

export default FooterContent
